/**
 * Step order for the enterprise /simulate wizard, and the rules for moving between steps.
 *
 * Two different code paths moved the user backwards through this wizard and only ONE of
 * them invalidated anything. The Review "Edit ✎" pencil trimmed the visited steps, so the
 * user had to come forward through the form again. The progress-stepper buttons called
 * setStepMapping directly, so: reach Review -> stepper back to "Complete Form" -> edit a
 * field -> stepper forward to "Review & Run" -> Review rendered, and Run submitted, the
 * PRE-EDIT payload. `savedFormPayload` is only written by the form's own Continue, so the
 * edit lived in `formState` and never reached the thing that was about to be billed
 * (#527, P1-03).
 *
 * Keeping the order in one exported constant matters as much as the rule: the two paths
 * previously each had their own copy of the list, which is how they drifted apart.
 */
export type SimulateStep =
  | 'dashboard' | 'use-case-form' | 'select-audience'
  | 'select-segments' | 'review-and-run' | 'simulation-results';

/**
 * `simulation-results` is ON the ladder, at the end.
 *
 * It was left off originally, which meant stepping back FROM the results screen was not
 * "backward" and so invalidated nothing: run a study, stepper back to Complete Form, edit
 * a field, stepper forward to Review & Run, and Review rendered the pre-edit
 * savedFormPayload again — the same stale-paid-run bug (#527 P1-03) the ladder exists to
 * prevent, reachable from the one screen the ladder did not cover.
 */
export const SIMULATE_STEP_ORDER: SimulateStep[] = [
  'dashboard', 'use-case-form', 'select-audience', 'select-segments', 'review-and-run',
  'simulation-results',
];

/**
 * Is `to` behind `from`? Only a BACKWARD move invalidates: going forward to a step you
 * have already completed is just navigation, and trimming there would make the wizard
 * un-finishable.
 *
 * A step outside the order (a build-audience step) is not comparable, so it is never
 * treated as backward — guessing would silently discard work.
 */
export function isBackwardStep(from: string, to: string): boolean {
  const fromIdx = SIMULATE_STEP_ORDER.indexOf(from as SimulateStep);
  const toIdx = SIMULATE_STEP_ORDER.indexOf(to as SimulateStep);
  if (fromIdx < 0 || toIdx < 0) return false;
  return toIdx < fromIdx;
}

/**
 * The visited set after stepping back to `target`: everything after it is dropped, so the
 * later steps go un-clickable and the user has to come forward through the form again —
 * which is what re-saves the payload Run will use.
 */
export function visitedStepsAfterEdit<T extends string>(visited: Set<T>, target: string): Set<T> {
  const targetIdx = SIMULATE_STEP_ORDER.indexOf(target as SimulateStep);
  if (targetIdx < 0) return visited;
  const keep = new Set<string>(SIMULATE_STEP_ORDER.slice(0, targetIdx + 1));
  const next = new Set<T>();
  visited.forEach((s) => { if (keep.has(s)) next.add(s); });
  return next;
}
